/**
 * @file DealDetailPage.tsx
 * @description Карточка сделки (phase09; phase19.31 — SC61, минимум по SC50:
 * карточку целиком перестраивает phase19.18).
 *
 * Шапка — в верхней панели (`CabinetPageHeader`): «назад» к реестру,
 * название, стадия пилюлей и видимые кнопки «Заказ Z-…», «Доска сделок»,
 * «Обновить» — без меню «⋯» (замечание владельца 2026-10-02 «достань все
 * кнопки из трёх точек»): «Сменить стадию» и «Заказ» — в панели от 1280,
 * остальные и всё до 1280 — строкой под шапкой (название важнее кнопок,
 * QA-5 D3/D4). Под ней — сведения одной
 * строкой (`InfoLine`) вместо трёх плиток, затем вкладки «Обзор / Позиции /
 * События / Документы» (общий `Tabs`, вкладка — в адресе `?tab=`).
 *
 * Связь с заказом (phase10.5 шаг 2.5D): заказ есть — кнопка «Заказ Z-…»;
 * заказа нет и сделка не проиграна — главное действие «Создать заказ»
 * (право `SHOP_MANAGE_ORDERS`).
 *
 * «Сменить стадию» (право `SHOP_USE_CRM`, стадия не закрыта) открывает
 * `DealStageMoveDialog`: при переносе в «Не состоялась» / «Отменена» окно
 * требует причину (сервер без неё отказывает `lost_reason_required`).
 */

import { Plus } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { useHasPermission } from "../../../../auth/hooks";
import { DealActivitiesTab } from "../../../../components/crm/DealActivitiesTab";
import { buildDealInfo } from "../../../../components/crm/deal-card/deal-info";
import { DealHeaderActions } from "../../../../components/crm/deal-card/DealHeaderActions";
import { DealStageMoveDialog } from "../../../../components/crm/deal-card/DealStageMoveDialog";
import { DealOverviewTab } from "../../../../components/crm/deal-card/DealOverviewTab";
import { useDealCard } from "../../../../components/crm/deal-card/useDealCard";
import { DealDocumentsTab } from "../../../../components/crm/DealDocumentsTab";
import { DealItemsTab } from "../../../../components/crm/DealItemsTab";
import { DealStagePill } from "../../../../components/crm/DealStagePill";
import { InfoLine } from "../../../../components/crm/InfoLine";
import { useTabParam } from "../../../../components/crm/useTabParam";
import { Button } from "../../../../components/ui/button";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import { ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { Tabs } from "../../../../components/ui/Tabs";
import type { DealDto } from "../../../../lib/api-crm";
import { canLeaveStage } from "../../../../lib/crm-deal-move";

const DEAL_TABS = ["overview", "items", "events", "documents"] as const;
type DealTab = (typeof DEAL_TABS)[number];

/** Заказ создавать нельзя, если сделка не состоялась или отменена. */
function canCreateOrderFor(deal: DealDto): boolean {
  return deal.currentStageKind !== "LOST" && deal.currentStageKind !== "CANCELLED";
}

export function DealDetailPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const base = `/shop/${slug}`;
  const card = useDealCard(id);
  const [tab, setTab] = useTabParam<DealTab>(DEAL_TABS);
  const canManageOrders = useHasPermission("SHOP_MANAGE_ORDERS");
  const canViewCustomers = useHasPermission("SHOP_VIEW_CUSTOMERS");
  const canUseCrm = useHasPermission("SHOP_USE_CRM");
  const [moving, setMoving] = useState(false);
  const { deal, order } = card;
  const back = { to: `${base}/crm/deals`, label: "К реестру сделок" };

  if (!deal) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Сделка" back={back} />
        {card.loading ? (
          <ListSkeleton rows={4} compact />
        ) : (
          <ListError message={card.error ?? "Сделка не найдена"} onRetry={card.refresh} />
        )}
      </div>
    );
  }

  const createOrder =
    !order && canManageOrders && canCreateOrderFor(deal) ? (
      <Button variant="brand" asChild>
        <Link to={`${base}/orders/new?dealId=${deal.id}&customerId=${deal.customerId}`}>
          <Plus className="size-4" />
          Создать заказ
        </Link>
      </Button>
    ) : undefined;
  // Кнопки шапки: название важнее кнопок (QA-5 D3/D4) — часть в панели от 1280.
  const headerActions = {
    order,
    orderHref: order ? `${base}/orders/${order.id}` : null,
    boardHref: `${base}/crm/board?pipelineId=${deal.pipelineId}`,
    onRefresh: card.refresh,
    refreshing: card.loading,
    onChangeStage: canUseCrm && canLeaveStage(deal.currentStageKind) ? () => setMoving(true) : undefined,
  };

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={deal.displayName}
        status={<DealStagePill deal={deal} />}
        back={back}
        panelActions={
          // Пустую часть заголовку не передаём (нет права сменить стадию и нет заказа).
          headerActions.onChangeStage || order ? <DealHeaderActions part="panel" {...headerActions} /> : undefined
        }
        secondaryActions={<DealHeaderActions part="row" {...headerActions} />}
        secondaryBelow
        action={createOrder}
      />

      <InfoLine
        aria-label="Сведения о сделке"
        items={buildDealInfo(deal, { customerHref: canViewCustomers ? `${base}/customers/${deal.customerId}` : null })}
      />

      <Tabs<DealTab>
        aria-label="Разделы карточки сделки"
        value={tab}
        onChange={setTab}
        items={[
          { value: "overview", label: "Обзор" },
          { value: "items", label: "Позиции" },
          { value: "events", label: "События", count: card.eventsCount },
          { value: "documents", label: "Документы", count: card.documentsCount },
        ]}
      />

      {tab === "overview" ? <DealOverviewTab deal={deal} /> : null}
      {tab === "items" ? <DealItemsTab deal={deal} onChanged={card.refresh} /> : null}
      {tab === "events" ? <DealActivitiesTab deal={deal} onChanged={card.refresh} /> : null}
      {tab === "documents" ? <DealDocumentsTab deal={deal} onChanged={card.refresh} /> : null}

      {moving ? (
        <DealStageMoveDialog
          deal={deal}
          onClose={() => setMoving(false)}
          onMoved={() => {
            setMoving(false);
            card.refresh();
          }}
        />
      ) : null}
    </div>
  );
}
